import React, { useCallback, useEffect, useRef, useState } from 'react'

import { ExpandableModalProps } from '../ExpandableModalMobile'
import { isLandscape, isPortrait } from './useOrientationChange'
import {
  computeLandscapeHeight,
  computePortraitHeight,
  useScrollableCheck,
} from './useScrollableCheck'

const MIN_MODAL_HEIGHT = 70
const MAX_MODAL_HEIGHT = 99
const MIN_MODAL_CLOSE_HEIGHT = 0
const MIN_MODAL_DRAG_HEIGHT = 45

const MODAL_DRAG_SLOWED = 8
const THRESHOLD = 10

export const useExpandableModal = (props: ExpandableModalProps) => {
  const initialHeightRef = useRef<number | null>(null)

  const scrollableCheckProps = useScrollableCheck()
  const modalStateProps = useModalState(scrollableCheckProps, initialHeightRef)
  const modalHandlersProps = useModalHandlers(modalStateProps, initialHeightRef)

  useModalResetEffect(modalStateProps, scrollableCheckProps, modalHandlersProps, initialHeightRef)

  const modalHandlersScrollable = useModalHandlersScrollable(
    props,
    modalStateProps,
    scrollableCheckProps,
    modalHandlersProps,
    initialHeightRef
  )
  const modalHandlersNotScrollable = useModalHandlersNotScrollable(
    props,
    modalStateProps,
    scrollableCheckProps,
    modalHandlersProps,
    initialHeightRef
  )

  const handleTouchStart = useCallback(
    async (event: React.TouchEvent) => {
      return isModalScrollable(modalStateProps.modalState)
        ? modalHandlersScrollable.handleTouchStart(event)
        : modalHandlersNotScrollable.handleTouchStart(event)
    },
    [modalStateProps.modalState, modalHandlersScrollable, modalHandlersNotScrollable]
  )

  const handleTouchMove = useCallback(
    async (event: React.TouchEvent) => {
      return isModalScrollable(modalStateProps.modalState)
        ? modalHandlersScrollable.handleTouchMove(event)
        : modalHandlersNotScrollable.handleTouchMove(event)
    },
    [modalStateProps.modalState, modalHandlersScrollable, modalHandlersNotScrollable]
  )

  const handleTouchEnd = useCallback(async () => {
    return isModalScrollable(modalStateProps.modalState)
      ? modalHandlersScrollable.handleTouchEnd()
      : modalHandlersNotScrollable.handleTouchEnd()
  }, [modalStateProps.modalState, modalHandlersScrollable, modalHandlersNotScrollable])

  const handleTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
    if (event.propertyName === 'height') {
      scrollableCheckProps.checkScroll()
    }
  }

  return {
    ...modalStateProps,
    ...scrollableCheckProps,
    modalRef: modalHandlersProps.modalRef,
    headerRef: modalHandlersProps.headerRef,
    footerRef: modalHandlersProps.footerRef,
    isShadowVisible:
      scrollableCheckProps.isInitialScrollable || isModalCompact(modalStateProps.modalState),
    isModalCompact: isModalCompact(modalStateProps.modalState),
    isModalDismissed: isModalDismissed(modalStateProps.modalState),
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleTransitionEnd,
  }
}

const useModalResetEffect = (
  modalStateProps: ReturnType<typeof useModalState>,
  scrollableCheckProps: ReturnType<typeof useScrollableCheck>,
  modalHandlersProps: ReturnType<typeof useModalHandlers>,
  initialHeightRef: React.MutableRefObject<number | null>
) => {
  const { resetModalState } = useModalEffect(
    modalStateProps,
    scrollableCheckProps,
    modalHandlersProps,
    initialHeightRef
  )

  useEffect(() => {
    const { isOrientationInProgress } = scrollableCheckProps
    if (!isOrientationInProgress && isModalActive(modalStateProps.modalState)) {
      const timer = setTimeout(resetModalState, 300)
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollableCheckProps.isOrientationInProgress])
}

export const useModalEffect = (
  modalStateProps: ReturnType<typeof useModalState>,
  scrollableCheckProps: ReturnType<typeof useScrollableCheck>,
  modalHandlersProps: ReturnType<typeof useModalHandlers>,
  initialHeightRef: React.MutableRefObject<number | null>
) => {
  const { modalState, isDragging } = modalStateProps
  const { initialModalPortraitHeight, initialModalLandscapeHeight } = modalStateProps
  const { initialModalPortraitState, initialModalLandscapeState } = modalStateProps
  const { setModalState, setModalHeight, setIsDragging } = modalStateProps
  const { setInitialModalLandscapeHeight, setInitialModalPortraitHeight } = modalStateProps
  const { setModalPortraitState, setModalLandscapeState } = modalStateProps

  const { modalRef, headerRef, footerRef, startYRef } = modalHandlersProps
  const { getNewModalHeightByModalRef } = modalHandlersProps

  const { isScrollable, scrollContentRef } = scrollableCheckProps
  const { isPortraitHeightChanged, isLandscapeHeightChanged } = scrollableCheckProps

  useEffect(() => {
    if (modalRef.current && !isDragging && isScrollable !== undefined) {
      if (isModalDismissed(modalState)) {
        return
      }

      if (isPortrait()) {
        if (isModalInitial(modalState) && detectIsModalCompactPortrait()) {
          const modalHeightPercent = getNewModalHeightByModalRef()
          initialHeightRef.current = modalHeightPercent

          setModalState(ModalState.Compact)
          setModalPortraitState(ModalState.Compact)
          setModalHeight(MIN_MODAL_HEIGHT)
        } else if (isModalInitial(modalState)) {
          const fullHeight = getModalFullPortraitHeight()
          initialHeightRef.current = fullHeight

          setModalState(ModalState.Auto)
          setModalPortraitState(ModalState.Auto)
          setModalHeight(fullHeight)
          !initialModalPortraitHeight && setInitialModalPortraitHeight(fullHeight)
        }
      }

      if (isLandscape()) {
        if (isModalInitial(modalState) && detectIsModalCompactLandscape()) {
          const modalHeightPercent = getNewModalHeightByModalRef()
          initialHeightRef.current = modalHeightPercent

          setModalState(ModalState.Compact)
          setModalLandscapeState(ModalState.Compact)
          setModalHeight(MIN_MODAL_HEIGHT)
        } else if (isModalInitial(modalState)) {
          const fullHeight = getModalFullPortraitHeight()
          initialHeightRef.current = fullHeight

          setModalState(ModalState.Auto)
          setModalLandscapeState(ModalState.Auto)
          setModalHeight(fullHeight)
          !initialModalLandscapeHeight && setInitialModalLandscapeHeight(fullHeight)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScrollable])

  const resetModalState = () => {
    setIsDragging(false)
    startYRef.current = null
    initialHeightRef.current = null

    if (isPortrait()) {
      resetPortraitMode()
    } else if (isLandscape()) {
      resetLandscapeMode()
    }
  }

  const resetPortraitMode = () => {
    if (initialModalPortraitState && initialModalPortraitHeight && !isPortraitHeightChanged()) {
      setModalState(initialModalPortraitState)
      setModalHeight(initialModalPortraitHeight)
      return
    }

    if (detectIsModalAutoPortrait()) {
      const fullHeight = getModalFullPortraitHeight()
      setModalState(ModalState.Auto)
      setModalHeight(fullHeight)
      setInitialModalPortraitHeight(fullHeight)
      setModalPortraitState(ModalState.Auto)
    } else {
      setModalState(ModalState.Compact)
      setModalHeight(MIN_MODAL_HEIGHT)
      setInitialModalLandscapeHeight(MIN_MODAL_HEIGHT)
      setModalLandscapeState(ModalState.Compact)
    }
  }

  const resetLandscapeMode = () => {
    if (initialModalLandscapeState && initialModalLandscapeHeight && !isLandscapeHeightChanged()) {
      setModalState(initialModalLandscapeState)
      setModalHeight(initialModalLandscapeHeight)
      return
    }

    if (detectIsModalAutoLandscape()) {
      const fullHeight = getModalFullLandscapeHeight()
      setModalState(ModalState.Auto)
      setModalHeight(fullHeight)
      setInitialModalLandscapeHeight(fullHeight)
      setModalLandscapeState(ModalState.Auto)
    } else {
      setModalState(ModalState.Compact)
      setModalHeight(MIN_MODAL_HEIGHT)
      setInitialModalLandscapeHeight(MIN_MODAL_HEIGHT)
      setModalLandscapeState(ModalState.Compact)
    }
  }

  const getModalFullPortraitHeight = () => {
    const fullModalHeight = getModalFullHeight()
    const newPortraitHeight = computePortraitHeight() || 0

    return (fullModalHeight / newPortraitHeight) * 100
  }

  const getModalFullLandscapeHeight = () => {
    const fullModalHeight = getModalFullHeight()
    const newLandscapeHeight = computeLandscapeHeight() || 0

    return (fullModalHeight / newLandscapeHeight) * 100
  }

  const getModalFullHeight = () => {
    const newModalContentHeight = scrollContentRef?.current?.clientHeight || 0

    const newModalHeaderHeight = headerRef?.current?.clientHeight || 0
    const newModalFooterHeight = footerRef?.current?.clientHeight || 0
    const modalFrameHeight = newModalHeaderHeight + newModalFooterHeight

    const fullModalHeight = modalFrameHeight + newModalContentHeight

    return fullModalHeight
  }

  const detectIsModalAutoPortrait = () => {
    const fullHeight = getModalFullPortraitHeight()
    return fullHeight < MIN_MODAL_HEIGHT
  }

  const detectIsModalCompactPortrait = () => {
    const fullHeight = getModalFullPortraitHeight()
    return fullHeight >= MIN_MODAL_HEIGHT
  }

  const detectIsModalAutoLandscape = () => {
    const fullHeight = getModalFullLandscapeHeight()
    return fullHeight < MIN_MODAL_HEIGHT
  }

  const detectIsModalCompactLandscape = () => {
    const fullHeight = getModalFullLandscapeHeight()
    return fullHeight >= MIN_MODAL_HEIGHT
  }

  return { resetModalState, getModalFullPortraitHeight, getModalFullLandscapeHeight }
}

export const useModalHandlersScrollable = (
  props: ExpandableModalProps,
  modalStateProps: ReturnType<typeof useModalState>,
  scrollableCheckProps: ReturnType<typeof useScrollableCheck>,
  modalHandlersProps: ReturnType<typeof useModalHandlers>,
  initialHeightRef: React.MutableRefObject<number | null>
) => {
  const { modalState, modalHeight } = modalStateProps
  const { setModalState, setModalHeight, setIsDragging } = modalStateProps
  const { getNewModalHeightByHeightRef } = modalHandlersProps
  const { startYRef } = modalHandlersProps
  const { isOrientationInProgress } = scrollableCheckProps

  const handleTouchStart = async (event: React.TouchEvent) => {
    if (isOrientationInProgress || !isModalActive(modalState)) {
      return
    }

    startYRef.current = event.touches[0].clientY
    initialHeightRef.current = modalHeight
  }

  const handleTouchMove = (event: React.TouchEvent) => {
    if (isOrientationInProgress || !isModalActive(modalState)) {
      return
    }

    setIsDragging(true)

    const updatedHeight = getNewModalHeightByTouchEvent(event)
    const limitedHeight = Math.max(MIN_MODAL_DRAG_HEIGHT, Math.min(updatedHeight, MAX_MODAL_HEIGHT))
    setModalHeight(limitedHeight)
  }

  const handleTouchEnd = async () => {
    if (isOrientationInProgress || isModalDismissed(modalState)) {
      return
    }

    setIsDragging(false)

    if (initialHeightRef.current == null) {
      startYRef.current = null
      return
    }

    const diff = getNewModalHeightByHeightRef()
    if (diff > THRESHOLD && isModalCompact(modalState)) {
      setModalState(ModalState.Expanded)
    } else if (diff < -THRESHOLD && isModalCompact(modalState)) {
      await onClose()
    } else if (modalHeight + THRESHOLD < MIN_MODAL_HEIGHT && isModalExpanded(modalState)) {
      await onClose()
    } else if (diff < -THRESHOLD && isModalExpanded(modalState)) {
      setModalState(ModalState.Compact)
    }

    startYRef.current = null
    initialHeightRef.current = null
  }

  const onClose = async () => {
    setModalState(ModalState.Dismissed)
    await new Promise((resolve) => setTimeout(resolve, 400))
    props.onClose?.()
  }

  const getNewModalHeightByTouchEvent = (event: React.TouchEvent): number => {
    if (startYRef.current !== null && initialHeightRef.current !== null) {
      const fingerYPosition = event.touches[0].clientY
      const fingerMoveDistance = startYRef.current - fingerYPosition
      return initialHeightRef.current + (fingerMoveDistance / window.innerHeight) * 100
    }
    return modalHeight
  }

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  }
}

export const useModalHandlersNotScrollable = (
  props: ExpandableModalProps,
  modalStateProps: ReturnType<typeof useModalState>,
  scrollableCheckProps: ReturnType<typeof useScrollableCheck>,
  modalHandlersProps: ReturnType<typeof useModalHandlers>,
  initialHeightRef: React.MutableRefObject<number | null>
) => {
  const { modalState, modalHeight } = modalStateProps
  const { setModalState, setModalHeight, setIsDragging } = modalStateProps
  const { getNewModalHeightByHeightRef } = modalHandlersProps
  const { startYRef } = modalHandlersProps
  const { isOrientationInProgress } = scrollableCheckProps

  const handleTouchStart = async (event: React.TouchEvent) => {
    if (isOrientationInProgress || !isModalActive(modalState)) {
      return
    }

    startYRef.current = event.touches[0].clientY
    initialHeightRef.current = modalHeight
  }

  const handleTouchMove = (event: React.TouchEvent) => {
    if (isOrientationInProgress || !isModalActive(modalState)) {
      return
    }

    setIsDragging(true)
    const updatedHeight = getNewModalHeightByTouchEvent(event)
    const limitedHeight = Math.max(0, Math.min(updatedHeight, 100))
    setModalHeight(limitedHeight)
  }

  const handleTouchEnd = async () => {
    if (isOrientationInProgress || !isModalActive(modalState)) {
      return
    }

    setIsDragging(false)

    if (initialHeightRef.current == null) {
      startYRef.current = null
      return
    }

    const diff = getNewModalHeightByHeightRef()
    if (diff < -THRESHOLD && isModalAuto(modalState)) {
      await onClose()
    }

    startYRef.current = null
    initialHeightRef.current = null
  }

  const onClose = async () => {
    setModalState(ModalState.Dismissed)
    await new Promise((resolve) => setTimeout(resolve, 400))
    props.onClose?.()
  }

  const getNewModalHeightByTouchEvent = (event: React.TouchEvent): number => {
    if (startYRef.current !== null && initialHeightRef.current !== null) {
      const fingerYPosition = event.touches[0].clientY
      const fingerMoveDistance = startYRef.current - fingerYPosition

      const upwardMoveDistanceSlowed = fingerMoveDistance / MODAL_DRAG_SLOWED
      const adjustedMoveDistance =
        fingerMoveDistance > 0 ? upwardMoveDistanceSlowed : fingerMoveDistance

      return initialHeightRef.current + (adjustedMoveDistance / window.innerHeight) * 100
    }

    return modalHeight
  }

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  }
}

export const useModalHandlers = (
  modalStateProps: ReturnType<typeof useModalState>,
  initialHeightRef: React.MutableRefObject<number | null>
) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)
  const startYRef = useRef<number | null>(null)

  const { modalHeight } = modalStateProps

  const getNewModalHeightByModalRef = () => {
    const modalHeight = modalRef?.current?.getBoundingClientRect().height || 0
    const windowHeight = window.innerHeight
    return (modalHeight / windowHeight) * 100
  }

  const getNewModalHeightByHeightRef = () => {
    const initialHeight = initialHeightRef?.current || 0
    return modalHeight - initialHeight
  }

  return {
    modalRef,
    headerRef,
    footerRef,
    startYRef,
    initialHeightRef,
    getNewModalHeightByModalRef,
    getNewModalHeightByHeightRef,
  }
}

export enum ModalState {
  Initial = 'initial',
  Compact = 'compact',
  Expanded = 'expanded',
  Dismissed = 'dismissed',
  Auto = 'auto',
}

export const useModalState = (
  scrollableCheckProps: ReturnType<typeof useScrollableCheck>,
  initialHeightRef: React.MutableRefObject<number | null>
) => {
  const { isOrientationInProgress } = scrollableCheckProps

  const [modalState, setModalState] = useState<ModalState>(ModalState.Initial)
  const [initialModalPortraitState, setModalPortraitState] = useState<ModalState | undefined>()
  const [initialModalLandscapeState, setModalLandscapeState] = useState<ModalState | undefined>()

  const [modalHeight, setModalHeight] = useState<number>(MIN_MODAL_HEIGHT)
  const [initialModalPortraitHeight, setInitialModalPortraitHeight] = useState<number | undefined>()
  const [initialModalLandscapeHeight, setInitialModalLandscapeHeight] = useState<
    number | undefined
  >()
  const [isDragging, setIsDragging] = useState<boolean>(false)

  useEffect(() => {
    if (isModalExpanded(modalState)) {
      setModalHeight(MAX_MODAL_HEIGHT)
    } else if (isModalCompact(modalState)) {
      setModalHeight(MIN_MODAL_HEIGHT)
    } else if (isModalAuto(modalState) && isDragging) {
      initialHeightRef?.current && setModalHeight(initialHeightRef.current)
    } else if (isModalAuto(modalState) && !isDragging && !isOrientationInProgress) {
      if (isPortrait()) {
        initialModalPortraitHeight && setModalHeight(initialModalPortraitHeight)
      }

      if (isLandscape() && !isOrientationInProgress) {
        initialModalLandscapeHeight && setModalHeight(initialModalLandscapeHeight)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalState, isDragging])

  return {
    isDragging,
    modalState,
    modalHeight,
    setIsDragging,
    setModalState,
    setModalHeight,
    initialModalPortraitHeight,
    setInitialModalPortraitHeight,
    initialModalLandscapeHeight,
    setInitialModalLandscapeHeight,
    initialModalPortraitState,
    setModalPortraitState,
    initialModalLandscapeState,
    setModalLandscapeState,
  }
}

export const isModalInitial = (state?: ModalState) => {
  return state === ModalState.Initial
}

export const isModalExpanded = (state?: ModalState) => {
  return state === ModalState.Expanded
}

export const isModalCompact = (state?: ModalState): state is ModalState => {
  return state === ModalState.Compact
}

export const isModalDismissed = (state?: ModalState): state is ModalState => {
  return state === ModalState.Dismissed
}

export const isModalAuto = (state?: ModalState): state is ModalState => {
  return state === ModalState.Auto
}

const isModalScrollable = (state?: ModalState): state is ModalState => {
  return isModalCompact(state) || isModalExpanded(state)
}

const isModalActive = (state?: ModalState): state is ModalState => {
  return isModalExpanded(state) || isModalCompact(state) || isModalAuto(state)
}

export const EXPANDABLE_MODAL_HEIGHT_CONSTANTS = {
  MIN_MODAL_HEIGHT,
  MAX_MODAL_HEIGHT,
  MIN_MODAL_CLOSE_HEIGHT,
  MIN_MODAL_DRAG_HEIGHT,
  MODAL_DRAG_SLOWED,
  THRESHOLD,
}
